import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { shouldRedirectToOnboarding } from '@/utils/onboardingUtils';

export interface Profile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  avatar_url?: string;
  date_of_birth?: string;
  family_role?: string;
}

export interface Family {
  id: string;
  name: string;
  created_by: string;
  owner_id?: string;
  plan_type?: string;
  created_at?: string;
  updated_at?: string;
}

export interface FamilyMember {
  id: string;
  role: 'father' | 'mother' | 'son' | 'daughter' | 'grandfather' | 'grandmother' | 'guardian_male' | 'guardian_female' | 'parent' | 'spouse' | 'child' | 'grandparent' | 'guardian';
  is_creator: boolean;
  child_name?: string;
  child_birth_date?: string;
  child_gender?: 'male' | 'female' | 'other' | 'prefer_not_to_say';
  child_avatar_url?: string;
  profile_id?: string;
  profiles?: Profile;
  family_id: string;
  created_at: string;
  updated_at: string;
}

interface FamilyWithMembership {
  family: Family;
  membership: {
    role: string;
    joined_at: string;
  };
}

export function useFamily() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [family, setFamily] = useState<Family | null>(null);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [loading, setLoading] = useState(true);
  
  // New family management state
  const [families, setFamilies] = useState<FamilyWithMembership[]>([]);
  const [currentFamily, setCurrentFamily] = useState<FamilyWithMembership | null>(null);

  // Fetch all user families
  const fetchUserFamilies = async () => {
    if (!user) return;

    try {
      const { data: familyMemberships, error } = await supabase
        .from('family_memberships')
        .select(`
          role,
          joined_at,
          families:family_id (
            id,
            name,
            owner_id,
            plan_type,
            created_by,
            created_at,
            updated_at
          )
        `)
        .eq('user_id', user.id);

      if (error) throw error;

      const familiesWithMembership = familyMemberships?.map(fm => ({
        family: fm.families,
        membership: {
          role: fm.role,
          joined_at: fm.joined_at
        }
      })).filter(f => f.family) || [];

      setFamilies(familiesWithMembership);

      // Set current family from localStorage or first available
      const savedFamilyId = localStorage.getItem('currentFamilyId');
      let current = null;
      
      if (savedFamilyId) {
        current = familiesWithMembership.find(f => f.family.id === savedFamilyId);
      }
      
      if (!current && familiesWithMembership.length > 0) {
        current = familiesWithMembership[0];
        localStorage.setItem('currentFamilyId', current.family.id);
      }

      setCurrentFamily(current);
      
      // Set backward compatibility family
      if (current) {
        setFamily(current.family);
        
        // Skip onboarding check if this is a brand new user signup in progress
        const isNewUserSignup = sessionStorage.getItem('isNewUserSignup') === 'true';
        if (!isNewUserSignup) {
          // Check if onboarding is needed for this family
          const needsOnboarding = await shouldRedirectToOnboarding(current.family.id, current.family.name);
          if (needsOnboarding) {
            console.log('🎯 [UseFamily] Onboarding needed for family:', current.family.name);
          }
        } else {
          console.log('🚫 [UseFamily] Skipping onboarding check - new user signup in progress');
        }
      }

    } catch (error) {
      console.error('Error fetching user families:', error);
      setFamilies([]);
      setCurrentFamily(null);
    }
  };

  const fetchUserData = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Fetch user profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (profileError) throw profileError;
      setProfile(profileData);

      // Fetch families
      await fetchUserFamilies();

      // If we have a current family, fetch its members (filtered for direct family only)
      if (currentFamily || (families.length > 0 && !currentFamily)) {
        const familyToUse = currentFamily || families[0];
        if (familyToUse) {
          const { data: familyMemberProfiles, error: membersError } = await supabase
            .from('person_profiles')
            .select('*')
            .eq('family_id', familyToUse.family.id);

          if (!membersError && familyMemberProfiles) {
            // Filter only direct family roles to match previous behavior
            const directFamilyRoles = ['father', 'mother', 'parent', 'spouse', 'son', 'daughter', 'child'];
            
            const filteredMembers = familyMemberProfiles
              .filter(p => p.category === 'family' && directFamilyRoles.includes(p.family_role))
              .map(p => ({
                id: p.id,
                family_id: p.family_id,
                profile_id: p.profile_id,
                role: p.family_role || 'child',
                is_creator: p.created_by === profileData.id,
                child_age: null,
                child_gender: p.gender,
                child_birth_date: p.date_of_birth,
                created_at: p.created_at,
                updated_at: p.updated_at,
                child_name: `${p.first_name} ${p.last_name}`,
                child_avatar_url: p.avatar_url
              }));

            // Remove duplicates based on profile_id or full name for those without profile_id
            const uniqueMembers = filteredMembers.reduce((acc, member) => {
              const existingIndex = acc.findIndex(existing => 
                (member.profile_id && existing.profile_id && member.profile_id === existing.profile_id) ||
                (!member.profile_id && !existing.profile_id && member.child_name === existing.child_name)
              );
              
              if (existingIndex === -1) {
                acc.push(member);
              } else {
                // Keep the one with more complete data (has profile_id or avatar)
                if (member.profile_id && !acc[existingIndex].profile_id) {
                  acc[existingIndex] = member;
                } else if (member.child_avatar_url && !acc[existingIndex].child_avatar_url) {
                  acc[existingIndex] = member;
                }
              }
              return acc;
            }, [] as typeof filteredMembers);

            setFamilyMembers(uniqueMembers);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Create a new family
  const createFamily = async (familyName?: string) => {
    if (!user) throw new Error('User must be authenticated');

    try {
      const { data, error } = await supabase.rpc('create_family_with_membership', {
        family_name: familyName || 'Ma Famille'
      });

      if (error) throw error;

      toast({
        title: "Famille créée avec succès !",
        description: `La famille "${(data as any).name || familyName}" a été créée.`,
      });

      // Refresh families list
      await fetchUserFamilies();
      
      return data;
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message || "Impossible de créer la famille.",
        variant: "destructive",
      });
      throw error;
    }
  };

  // Switch to a different family
  const switchFamily = async (familyId: string) => {
    const targetFamily = families.find(f => f.family.id === familyId);
    if (!targetFamily) return;

    setCurrentFamily(targetFamily);
    setFamily(targetFamily.family);
    localStorage.setItem('currentFamilyId', familyId);

    // Refresh family data
    setLoading(true);
    await fetchUserData();
    
    toast({
      title: "Famille changée",
      description: `Vous êtes maintenant dans la famille "${targetFamily.family.name}".`,
    });
  };

  // Update family name
  const updateFamily = async (familyId: string, name: string) => {
    try {
      const { error } = await supabase
        .from('families')
        .update({ name, updated_at: new Date().toISOString() })
        .eq('id', familyId);

      if (error) throw error;

      toast({
        title: "Famille mise à jour",
        description: `Le nom de la famille a été changé pour "${name}".`,
      });

      await fetchUserFamilies();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message || "Impossible de mettre à jour la famille.",
        variant: "destructive",
      });
      throw error;
    }
  };

  // Delete family
  const deleteFamily = async (familyId: string) => {
    try {
      const { error } = await supabase
        .from('families')
        .delete()
        .eq('id', familyId);

      if (error) throw error;

      toast({
        title: "Famille supprimée",
        description: "La famille a été supprimée avec succès.",
      });

      // If we deleted the current family, switch to another one
      if (currentFamily?.family.id === familyId) {
        localStorage.removeItem('currentFamilyId');
        
        const remainingFamilies = families.filter(f => f.family.id !== familyId);
        if (remainingFamilies.length > 0) {
          const nextFamily = remainingFamilies[0];
          localStorage.setItem('currentFamilyId', nextFamily.family.id);
          setCurrentFamily(nextFamily);
          setFamily(nextFamily.family);
          await fetchUserData();
        } else {
          if (window.location.pathname !== '/new-user-onboarding') {
            window.location.href = '/new-user-onboarding';
          }
        }
      } else {
        await fetchUserFamilies();
      }
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message || "Impossible de supprimer la famille.",
        variant: "destructive",
      });
      throw error;
    }
  };

  useEffect(() => {
    if (user) {
      fetchUserData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const refreshData = () => {
    fetchUserData();
  };

  return {
    profile,
    family,
    familyMembers,
    loading,
    
    // New family management
    families,
    currentFamily,
    createFamily,
    switchFamily,
    updateFamily,
    deleteFamily,
    refreshData
  };
}